import { useMemo, type FC } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { Button, DataTable, Input, Pagination, Select } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import {
  buildAffiliateProfilePath,
  NAVIGATION_ROUTES,
} from "../../../utils/NavigationUtils";

import { formatNumber, type AffiliateRow } from "../affiliatesData";
import { buildAffiliateMetricColumns } from "../sharedPromoCodeColumns";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";
import AffiliateStatusBadge from "../AffiliateStatusBadge/AffiliateStatusBadge";
import type { OperatorAffiliateListItem } from "../../../types/affiliates";

import styles from "./AffiliatesList.module.css";

export type AffiliatesListPagination = {
  total: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (nextPage: number) => void;
  onPageSizeChange: (nextPageSize: number) => void;
};

export type AffiliatesListFilters = {
  search: string;
  onSearchChange: (next: string) => void;
  status: AffiliateRow["status"] | undefined;
  onStatusChange: (next: AffiliateRow["status"] | undefined) => void;
};

export type AffiliatesListProps = {
  rows: AffiliateRow[];
  loading: boolean;
  filters: AffiliatesListFilters;
  pagination: AffiliatesListPagination;
};

// The list-scoped date picker and CSV download button live on the page
// header (rendered by `Affiliates`) so they sit next to the page title
// instead of in the row that also carries the search input + status
// filter + "New Affiliate" CTA. The parent already owns the date range,
// the debounced search, and the download mutation, so this component
// stays focused on rendering the table + its row-level filters.

// Row carries the raw API plan_type so the formatter can degrade missing
// values to "-" instead of leaking a placeholder string.
const formatPaymentModel = (
  planType: OperatorAffiliateListItem["plan_type"],
) => {
  if (planType === "CPA") return AppStrings.AFFILIATE_PAYMENT_CPA;
  if (planType === "REVENUE_SHARE")
    return AppStrings.AFFILIATE_PAYMENT_REV_SHARE;
  if (planType === "HYBRID") return AppStrings.AFFILIATE_PAYMENT_HYBRID;
  return AppStrings.EMPTY_REASON;
};

const AffiliatesList: FC<AffiliatesListProps> = ({
  rows,
  loading,
  filters,
  pagination,
}) => {
  const navigate = useNavigate();
  const currencySymbol = useCurrencySymbol() ?? "$";

  const handleNewAffiliate = () => {
    navigate(NAVIGATION_ROUTES.MARKETING_AFFILIATE_NEW);
  };

  const columns = useMemo<Array<Record<string, unknown>>>(
    () => [
      {
        title: AppStrings.ID,
        dataIndex: "id",
        key: "id",
        width: 120,
      },
      {
        title: AppStrings.NAME,
        dataIndex: "name",
        key: "name",
        width: 180,
        render: (name: string | undefined, record: AffiliateRow) => {
          if (!name) return null;
          const targetId = record.id ?? record.key;
          return (
            <button
              type="button"
              className={styles.nameLink}
              onClick={() => navigate(buildAffiliateProfilePath(targetId))}
            >
              {name}
            </button>
          );
        },
      },
      {
        title: AppStrings.AFFILIATE_PAYMENT_MODEL,
        dataIndex: "paymentModel",
        key: "paymentModel",
        width: 140,
        render: (value: AffiliateRow["paymentModel"]) =>
          formatPaymentModel(value ?? null),
      },
      {
        title: AppStrings.PROMO_CODE_COUNT,
        dataIndex: "promoCodeCount",
        key: "promoCodeCount",
        width: 160,
        render: (count: number | undefined) => (
          <span className={styles.promoCodeCount}>{formatNumber(count ?? 0)}</span>
        ),
      },
      ...buildAffiliateMetricColumns(currencySymbol),
      {
        title: AppStrings.STATUS,
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (value: AffiliateRow["status"]) =>
          value ? <AffiliateStatusBadge status={value} /> : null,
      },
    ],
    [navigate, currencySymbol],
  );

  return (
    <>
      <div className={styles.searchSection}>
        <div className={styles.searchInputs}>
          <Input
            variant="search"
            className={styles.searchInput}
            placeholder={AppStrings.SEARCH_BY_NAME}
            value={filters.search}
            onChange={(e) => filters.onSearchChange(e.target.value)}
            allowClear
          />
          <Select<AffiliateRow["status"]>
            className={styles.statusFilter}
            placeholder={AppStrings.STATUS}
            value={filters.status}
            onChange={(value) => filters.onStatusChange(value)}
            allowClear
            options={[
              {
                value: "active",
                label: <AffiliateStatusBadge status="active" />,
              },
              {
                value: "deactivated",
                label: <AffiliateStatusBadge status="deactivated" />,
              },
            ]}
          />
        </div>

        <div className={styles.searchActions}>
          <Button size="large" onClick={handleNewAffiliate}>
            <PlusOutlined className={styles.newAffiliateIcon} />
            <span>{AppStrings.NEW_AFFILIATE}</span>
          </Button>
        </div>
      </div>

      <div className={styles.tableSection}>
        <DataTable<AffiliateRow>
          rowKey="key"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          expandable={{
            rowExpandable: (record: AffiliateRow) =>
              Boolean(record.children?.length),
          }}
        />
        <Pagination
          total={pagination.total}
          pageSize={pagination.pageSize}
          currentPage={pagination.currentPage}
          onPageSizeChange={pagination.onPageSizeChange}
          onPageChange={pagination.onPageChange}
          itemLabel="affiliates"
          restrictToAdjacentPages
        />
      </div>
    </>
  );
};

export default AffiliatesList;
